import { useState, useEffect, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  MessageCircle, 
  MapPin, 
  Clock, 
  Scissors, 
  Star,
  ChevronDown,
  Menu,
  Sun,
  Moon,
  Facebook,
  ExternalLink
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import "@/App.css";

import heroImage from './assets/fo_kep.webp';
import barberImage from './assets/toth_robi_portre.webp';
import g1 from './assets/kep1.webp';
import g2 from './assets/kep2.webp';
import g3 from './assets/kep3.webp';
import g4 from './assets/kep4.webp';
import g5 from './assets/kep5.webp';
import g6 from './assets/tovabbi_kep1.webp';
import g7 from './assets/tovabbi_kep2.webp';
import g8 from './assets/tovabbi_kep3.webp';
import g9 from './assets/tovabbi_kep4.webp';
import g10 from './assets/tovabbi_kep5.webp';

// Images from design guidelines
const IMAGES = {
  hero: heroImage,
  barber: barberImage,
  gallery1: g1,
  gallery2: g2,
  gallery3: g3,
  gallery4: g4,
  gallery5: g5,
  gallery6: g6,
  gallery7: g7,
  gallery8: g8,
  gallery9: g9,
  gallery10: g10
};

// Common theme classes
const THEME = {
  // Backgrounds
  bgPrimary: "bg-stone-50 dark:bg-obsidian",
  bgSecondary: "bg-stone-100 dark:bg-obsidian-light",
  bgCard: "bg-white dark:bg-obsidian-light",
  bgCardAlt: "bg-white dark:bg-obsidian",
  // Text
  textPrimary: "text-stone-900 dark:text-white",
  textSecondary: "text-stone-600 dark:text-zinc-400",
  textMuted: "text-stone-500 dark:text-zinc-500",
  textDim: "text-stone-400 dark:text-zinc-600",
  // Borders
  border: "border-stone-200 dark:border-white/10",
  borderLight: "border-stone-200 dark:border-white/5",
  borderDotted: "border-stone-300 dark:border-zinc-700",
  divider: "text-stone-300 dark:text-zinc-700",
};

// Services data
const SERVICES = [
  { id: 1, name: "Férfi Hajvágás", subtitle: "CLASSIC CUT", desc: "Precíziós vágás az egyéni karakteredre építve.", price: "3.500 Ft" },
  { id: 2, name: "Gyermek Hajvágás", subtitle: "JUNIOR STYLE", desc: "Barátságos környezet a legkisebbeknek is, 5 éves kortól.", price: "3.500 Ft" },
  { id: 3, name: "Apa-Fia Páros", subtitle: "GENERATIONS", desc: "Közös stílus, közös élmény.", price: "5.000 Ft" },
  { id: 4, name: "Szakállvágás", subtitle: "BEARD ART", desc: "Pengeéles kontúrok.", price: "1.500 Ft" },
  { id: 5, name: "Női Pixie Vágás", subtitle: "FEMME FATALE", desc: "Bátor és elegáns rövid formák.", price: "4.500 Ft" },
];

// Testimonials data
const TESTIMONIALS = [
  { id: 1, name: "Kovács Péter", text: "Profi munka, remek hangulat. Mindig pontosan azt kapom, amit kérek. Debrecen legjobbja!" },
  { id: 2, name: "Nagy Tamás", text: "A kávé isteni, a hajvágás pedig milliméter pontos. Robi nagyon figyel a részletekre." },
  { id: 3, name: "Szabó Márk", text: "Magas színvonalú munka, igényes környezet és gördülékeny kiszolgálás. Pontosan azt kaptam, amit elképzeltem, minden részletre odafigyeltek." },
];

// FAQ data
const FAQ_DATA = [
  { q: "Mivel tudok fizetni az üzletben?", a: "Üzletünkben jelenleg csak készpénzzel tudsz fizetni." },
  { q: "Hogyan érkezzek a hajvágásra?", a: "Kérjük, időben érkezz a lefoglalt időpontra, hogy kényelmesen el tudjuk kezdeni a hajvágást. Ha bizonytalan vagy a frizurában, nyugodtan hozz inspirációs képet." },
  { q: "Van parkolási lehetőség?", a: "Igen! A Derék utcán rengeteg ingyenes parkolóhely áll rendelkezésre közvetlenül az üzlet előtt." },
  { q: "Lemondási feltételek", a: "A lefoglalt időpontot legkésőbb 24 órával előtte tudod lemondani vagy módosítani telefonon vagy Messenger üzenetben. Ennek elmulasztása esetén a következő foglalásnál kérhetjük a szolgáltatás előre történő kifizetését." },
];

// Features data
const FEATURES = [
  { icon: "📏", title: "Precizitás", desc: "Milliméterpontos vágások minden alkalommal." },
  { icon: "🧴", title: "Prémium", desc: "Csúcsminőségű hajápolók a hajad egészségéért." },
  { icon: "☕", title: "Élmény", desc: "Kávé és stílus egy exkluzív környezetben." },
  { icon: "✂️", title: "Trend", desc: "Folyamatosan követem a legújabb trendeket." },
  { icon: "✨", title: "Higiénia", desc: "Maximális sterilitás minden eszközünknél." },
  { icon: "🤝", title: "Tanácsadás", desc: "Segítek kiválasztani a karakteredhez illő formát." },
];

// Animation variants
const fadeUpVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Reveal component with intersection observer
const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeUpVariant}
      transition={{ delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Navigation component
const Navigation = ({ scrolled, isDark, toggleTheme, openBooking }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const navLinks = [
    { href: "#about", label: "Rólam" },
    { href: "#services", label: "Szolgáltatások" },
    { href: "#gallery", label: "Galéria" },
    { href: "#location", label: "Helyszín" },
    { href: "#faq", label: "FAQ" },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileOpen(false);
    }
  };
  
  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/85 dark:bg-black/85 backdrop-blur-xl border-b border-stone-200 dark:border-white/5 py-3' 
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between">
        {/* Logo */}
        <a 
          href="#hero" 
          onClick={(e) => scrollToSection(e, '#hero')}
          className="font-serif text-xl sm:text-2xl font-bold tracking-[0.15em] text-gold"
          data-testid="logo"
        >
          TR FODRÁSZAT
        </a>
        
        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map(link => (
            <a 
              key={link.href}
              href={link.href}
              onClick={(e) => scrollToSection(e, link.href)}
              className={`text-sm font-sans font-medium tracking-wide ${scrolled ? 'text-stone-700 dark:text-zinc-300' : 'text-stone-100 dark:text-zinc-300'} hover:text-gold transition-colors gold-underline`}
              data-testid={`nav-${link.href.slice(1)}`}
            >
              {link.label}
            </a>
          ))}
          
          {/* Theme Toggle */}
          <button 
            onClick={toggleTheme}
            className={`p-2 ${scrolled ? 'text-stone-700 dark:text-zinc-300' : 'text-stone-100 dark:text-zinc-300'} hover:text-gold transition-colors`}
            data-testid="theme-toggle"
            aria-label="Téma váltás"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          
          {/* CTA Button */}
          <button 
            onClick={openBooking}
            className="bg-gold text-black font-sans font-bold text-xs tracking-[0.1em] uppercase px-6 py-3 hover:bg-gold-dark transition-colors"
            data-testid="nav-booking-btn"
          >
            Időpontfoglalás
          </button>
        </div>
        
        {/* Mobile Menu */}
        <div className="lg:hidden flex items-center gap-4">
          <button 
            onClick={toggleTheme}
            className={`p-2 ${scrolled ? 'text-stone-700 dark:text-zinc-300' : 'text-stone-100 dark:text-zinc-300'} hover:text-gold transition-colors`}
            data-testid="theme-toggle-mobile"
            aria-label="Téma váltás"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button 
                className={`p-2 ${scrolled ? 'text-stone-900 dark:text-white' : 'text-white'}`}
                data-testid="mobile-menu-btn"
                aria-label="Menü megnyitása"
              >
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white dark:bg-obsidian border-l border-stone-200 dark:border-white/10 w-[300px]">
              <SheetTitle className="sr-only">Navigációs menü</SheetTitle>
              <div className="flex flex-col gap-8 mt-12">
                {navLinks.map(link => (
                  <a 
                    key={link.href}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-lg font-sans font-medium text-stone-900 dark:text-white hover:text-gold transition-colors"
                    data-testid={`mobile-nav-${link.href.slice(1)}`}
                  >
                    {link.label}
                  </a>
                ))}
                <button 
                  onClick={() => { openBooking(); setMobileOpen(false); }}
                  className="bg-gold text-black font-sans font-bold text-sm tracking-[0.1em] uppercase px-6 py-4 mt-4 hover:bg-gold-dark transition-colors"
                  data-testid="mobile-booking-btn"
                >
                  Időpontfoglalás
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = ({ openBooking }) => {
  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${IMAGES.hero})` }}
      />
      {/* Dark Overlay - same in both modes for readability */}
      <div className="absolute inset-0 bg-black/70" />
      
      {/* Gold accent line */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-32 bg-gold hidden lg:block" />
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <p className="text-xs font-sans uppercase tracking-[0.3em] text-gold mb-6">
            Debrecen • Derék utca 36
          </p>
          {/*<h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-bold text-white leading-[0.9] tracking-tight mb-8">
            PURE<br />
            <span className="text-gold">PRECISION</span>
          </h1>*/}
          <p className="text-white text-2xl sm:text-4xl font-serif tracking-[0.2em] uppercase mb-10 mx-auto drop-shadow-sm">
            Üdvözöllek a <span className="text-gold italic">Weboldalamon!</span>
          </p>
          <p className="text-zinc-300 text-lg sm:text-xl font-sans max-w-xl mx-auto mb-10">
            Stílusos fodrászat férfiaknak és nőknek – ahol a precíz munka és az igényes megjelenés alap.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={openBooking}
              className="bg-gold text-black font-sans font-bold text-sm tracking-[0.1em] uppercase px-8 py-4 hover:bg-gold-dark transition-colors w-full sm:w-auto"
              data-testid="hero-booking-btn"
            >
              Foglalj Időpontot
            </button>
            <a 
              href="https://www.facebook.com/profile.php?id=61574330504921" 
              target="_blank" 
              rel="noopener noreferrer"
              className="border border-gold text-gold font-sans font-bold text-sm tracking-[0.1em] uppercase px-8 py-4 hover:bg-gold/10 transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
              data-testid="hero-facebook-btn"
            >
              <Facebook className="w-4 h-4" /> Facebook
            </a>
          </div>
        </motion.div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ChevronDown className="w-6 h-6 text-gold" />
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className={`py-24 sm:py-32 ${THEME.bgPrimary}`} data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <Reveal>
            <div className="relative overflow-hidden group">
              <img 
                src={IMAGES.barber} 
                alt="Tóth Robi fodrász"
                className="w-full h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 border border-gold/20" />
              {/* Gold corner accents */}
              <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-gold" />
              <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-gold" />
            </div>
          </Reveal>
          
          {/* Content */}
          <Reveal delay={0.2}>
            <div>
              <p className="text-xs font-sans uppercase tracking-[0.2em] text-gold mb-4">Rólam</p>
              <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold ${THEME.textPrimary} mb-6 leading-tight`}>
                Tóth Robi vagyok a TR Fodrászat hajszobrásza
              </h2>
              <div className={`space-y-4 ${THEME.textSecondary} font-sans leading-relaxed`}>
                <p>
                  Fodrászként nemcsak frizurákat készítek, hanem élményt is nyújtok minden vendégemnek. 
                  Nálam a hajvágás több, mint rutin – egy kis beszélgetés, jó hangulat és figyelem minden részletre.
                </p>
                <p>
                  Szeretettel várlak egy frissen főzött, fekete kávéval!
                </p>
              </div>
              
              {/* Stats */}
              <div className={`grid grid-cols-3 gap-6 mt-10 pt-10 border-t ${THEME.border}`}>
                <div>
                  <p className="font-serif text-3xl sm:text-4xl font-bold text-gold">3+</p>
                  <p className={`text-sm ${THEME.textMuted} mt-1`}>Év tapasztalat</p>
                </div>
                <div>
                  <p className="font-serif text-3xl sm:text-4xl font-bold text-gold">300+</p>
                  <p className={`text-sm ${THEME.textMuted} mt-1`}>Elégedett vendég</p>
                </div>
                <div>
                  <p className="font-serif text-3xl sm:text-4xl font-bold text-gold">5.0</p>
                  <p className={`text-sm ${THEME.textMuted} mt-1`}>Google értékelés</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  return (
    <section className={`py-24 sm:py-32 ${THEME.bgSecondary}`} data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <Reveal>
          <div className="text-center mb-16">
            <p className="text-xs font-sans uppercase tracking-[0.2em] text-gold mb-4">Értékek</p>
            <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold ${THEME.textPrimary}`}>
              Miért válassz engem?
            </h2>
          </div>
        </Reveal>
        
        <motion.div 
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {FEATURES.map((feature, index) => (
            <motion.div
              key={index}
              variants={fadeUpVariant}
              className={`${THEME.bgCardAlt} border ${THEME.borderLight} p-8 hover:border-gold/30 transition-colors group`}
              data-testid={`feature-card-${index}`}
            >
              <span className="text-4xl mb-4 block">{feature.icon}</span>
              <h3 className={`font-serif text-xl font-bold ${THEME.textPrimary} mb-2 group-hover:text-gold transition-colors`}>
                {feature.title}
              </h3>
              <p className={`${THEME.textMuted} text-sm leading-relaxed`}>{feature.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className={`py-24 sm:py-32 ${THEME.bgPrimary}`} data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <Reveal>
          <div className="text-center mb-16">
            <p className="text-xs font-sans uppercase tracking-[0.2em] text-gold mb-4">Árlista</p>
            <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold ${THEME.textPrimary} mb-4`}>
              Szolgáltatások
            </h2>
            <p className={THEME.textMuted}>Érvényes 2025.11.01-től</p>
          </div>
        </Reveal>
        
        <div className="max-w-3xl mx-auto space-y-4">
          {SERVICES.map((service, index) => (
            <Reveal key={service.id} delay={index * 0.1}>
              <div 
                className={`${THEME.bgCard} border ${THEME.borderLight} p-6 sm:p-8 hover:border-gold/30 transition-colors`}
                data-testid={`service-item-${service.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-sans uppercase tracking-[0.15em] text-gold mb-1">
                      {service.subtitle}
                    </p>
                    <h3 className={`font-serif text-xl sm:text-2xl font-bold ${THEME.textPrimary}`}>
                      {service.name}
                    </h3>
                    <p className={`${THEME.textMuted} text-sm mt-2`}>{service.desc}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className={`hidden sm:block flex-1 border-b border-dotted ${THEME.borderDotted} min-w-[50px]`} />
                    <p className="font-serif text-2xl sm:text-3xl font-bold text-gold whitespace-nowrap">
                      {service.price}
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        
        {/* Special CTA */}
        <Reveal delay={0.5}>
          <div className="mt-12 bg-gold p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6 max-w-3xl mx-auto">
            <div>
              <p className="text-xs font-sans uppercase tracking-[0.15em] text-black/60 mb-2">TR Special Service</p>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-black">Egyedi elképzelésed van?</h3>
              <p className="text-black/70 text-sm mt-1">Fotózásra készülsz? Megvalósítjuk!</p>
            </div>
            <a 
              href="https://www.facebook.com/profile.php?id=61574330504921" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-black text-white font-sans font-bold text-sm tracking-[0.1em] uppercase px-8 py-4 hover:bg-obsidian-light transition-colors whitespace-nowrap"
              data-testid="special-service-cta"
            >
              Kapcsolatfelvétel
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section className={`py-24 sm:py-32 ${THEME.bgSecondary}`} data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <Reveal>
          <div className="text-center mb-16">
            <p className="text-xs font-sans uppercase tracking-[0.2em] text-gold mb-4">Vélemények</p>
            <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold ${THEME.textPrimary}`}>
              Amit vendégeink mondanak
            </h2>
          </div>
        </Reveal>
        
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((testimonial, index) => (
            <Reveal key={testimonial.id} delay={index * 0.15}>
              <div 
                className={`${THEME.bgCardAlt} border ${THEME.borderLight} p-8 hover:border-gold/30 transition-colors h-full`}
                data-testid={`testimonial-${testimonial.id}`}
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-gold text-gold" />
                  ))}
                </div>
                <p className={`${THEME.textSecondary} italic leading-relaxed mb-6`}>"{testimonial.text}"</p>
                <p className={`font-sans font-bold ${THEME.textPrimary}`}>— {testimonial.name}</p>
              </div>
            </Reveal>
          ))}
        </div>
        
        <Reveal delay={0.4}>
          <div className="text-center mt-12">
            <a 
              href="https://www.google.com/maps/place/TR+Fodr%C3%A1szat/@47.5243649,21.5977789,17z/data=!4m8!3m7!1s0x47470f86846197f9:0xd081c12dc4909036!8m2!3d47.5243613!4d21.6003538!9m1!1b1!16s%2Fg%2F11lp87hgb0?entry=ttu&g_ep=EgoyMDI2MDQxNS4wIKXMDSoASAFQAw%3D%3D" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-gold text-gold font-sans font-bold text-sm tracking-[0.1em] uppercase px-8 py-4 hover:bg-gold/10 transition-colors"
              data-testid="all-reviews-btn"
            >
              Összes vélemény <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const [showAll, setShowAll] = useState(false);
  
  const galleryItems = [
    { src: IMAGES.gallery1, label: "THE LOOK" },
    { src: IMAGES.gallery2, label: "MODERN" },
    { src: IMAGES.gallery3, label: "Precision" },
    { src: IMAGES.gallery4, label: "KID" },
    { src: IMAGES.gallery5, label: "STYLE" },
    { src: IMAGES.gallery6, label: "GOLD" },
    { src: IMAGES.gallery7, label: "SIMPLE" },
    { src: IMAGES.gallery8, label: "JACK" },
    { src: IMAGES.gallery9, label: "SIMPLE" },
    { src: IMAGES.gallery10, label: "COFFE" },
  ];
  
  return (
    <section id="gallery" className={`py-24 sm:py-32 ${THEME.bgPrimary}`} data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <Reveal>
          <div className="text-center mb-16">
            <p className="text-xs font-sans uppercase tracking-[0.2em] text-gold mb-4">Portfolio</p>
            <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold ${THEME.textPrimary} mb-4`}>
              Galéria
            </h2>
            <p className={THEME.textMuted}>Tekintsd meg referencia munkáimat</p>
          </div>
        </Reveal>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {galleryItems.slice(0, showAll ? galleryItems.length : 3).map((item, index) => (
            <Reveal key={index} delay={index * 0.1}>
              <div 
                className="relative overflow-hidden group cursor-pointer aspect-square"
                data-testid={`gallery-item-${index}`}
              >
                <img 
                  src={item.src} 
                  alt={item.label}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="bg-black/60 backdrop-blur-sm text-white text-xs uppercase tracking-wider px-3 py-1">
                    {item.label}
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
          
          {!showAll && (
            <Reveal delay={0.3}>
              <button
                onClick={() => setShowAll(true)}
                className={`aspect-square border border-dashed ${THEME.borderDotted} flex flex-col items-center justify-center gap-3 hover:border-gold hover:bg-gold/5 transition-colors w-full`}
                data-testid="gallery-load-more"
              >
                <span className={`text-3xl ${THEME.textMuted}`}>+</span>
                <span className={`text-sm font-sans font-bold ${THEME.textMuted} uppercase tracking-wider`}>
                  További képek
                </span>
              </button>
            </Reveal>
          )}
          
          {showAll && (
            <Reveal delay={0.3}>
              <div 
                className="relative overflow-hidden group cursor-pointer aspect-square"
                data-testid="gallery-item-3"
              >
                <img 
                  src={galleryItems[3].src} 
                  alt={galleryItems[3].label}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="bg-black/60 backdrop-blur-sm text-white text-xs uppercase tracking-wider px-3 py-1">
                    {galleryItems[3].label}
                  </span>
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};

// Location Section
const LocationSection = () => {
  return (
    <section id="location" className={`py-24 sm:py-32 ${THEME.bgSecondary}`} data-testid="location-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <Reveal>
          <div className="text-center mb-16">
            <p className="text-xs font-sans uppercase tracking-[0.2em] text-gold mb-4">Helyszín</p>
            <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold ${THEME.textPrimary} mb-4`}>
              Üzletem helyszíne
            </h2>
          </div>
        </Reveal>
        
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Map */}
          <Reveal>
            <div className={`w-full h-[400px] overflow-hidden border ${THEME.border}`}>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2694.026130129219!2d21.58782407689975!3d47.528383893352724!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47470786846197f9%3A0x8081c12dc3909036!2sDebrecen%2C%20Der%C3%A9k%20u.%2036%2C%204031!5e0!3m2!1shu!2shu!4v1700000000000!5m2!1shu!2shu" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy"
                title="TR Fodrászat térkép"
                data-testid="location-map"
              />
            </div>
          </Reveal>
          
          {/* Info */}
          <Reveal delay={0.2}>
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-gold/10 text-gold">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h3 className={`font-serif text-xl font-bold ${THEME.textPrimary} mb-1`}>Cím</h3>
                  <p className={THEME.textSecondary}>4031 Debrecen, Derék utca 36.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="p-3 bg-gold/10 text-gold">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h3 className={`font-serif text-xl font-bold ${THEME.textPrimary} mb-1`}>Telefon</h3>
                  <a href="tel:+36501065988" className={`${THEME.textSecondary} hover:text-gold transition-colors`}>
                    +36 50 106 59 88
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="p-3 bg-gold/10 text-gold">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <h3 className={`font-serif text-xl font-bold ${THEME.textPrimary} mb-1`}>Nyitvatartás</h3>
                  <p className={THEME.textSecondary}>Hétfő - Vasárnap: Előzetes egyeztetés alapján</p>
                </div>
              </div>
              
              <div className={`pt-6 border-t ${THEME.border}`}>
                <p className={`${THEME.textMuted} text-sm`}>
                  Ingyenes parkolás közvetlenül az üzlet előtt a Derék utcán.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  return (
    <section id="faq" className={`py-24 sm:py-32 ${THEME.bgPrimary}`} data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 sm:px-12">
        <Reveal>
          <div className="text-center mb-16">
            <p className="text-xs font-sans uppercase tracking-[0.2em] text-gold mb-4">Info</p>
            <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold ${THEME.textPrimary}`}>
              Gyakori kérdések
            </h2>
          </div>
        </Reveal>
        
        <Reveal delay={0.2}>
          <Accordion type="single" collapsible className="space-y-4">
            {FAQ_DATA.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`} 
                className={`border ${THEME.border} ${THEME.bgCard} px-6 data-[state=open]:border-gold/40`}
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className={`text-left font-serif text-lg ${THEME.textPrimary} hover:text-gold hover:no-underline py-6`}>
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className={`${THEME.textSecondary} pb-6`}>
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ openLegalModal }) => {
  return (
    <footer className={`py-12 ${THEME.bgPrimary} border-t ${THEME.borderLight}`} data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl font-bold tracking-[0.15em] text-gold">
            TR FODRÁSZAT
          </p>
          
          <div className={`flex flex-wrap items-center justify-center gap-6 text-sm ${THEME.textMuted}`}>
            <button 
              onClick={() => openLegalModal('impresszum')} 
              className="hover:text-gold transition-colors"
              data-testid="footer-impresszum"
            >
              Impresszum
            </button>
            <span className={THEME.divider}>•</span>
            <button 
              onClick={() => openLegalModal('adatkezeles')} 
              className="hover:text-gold transition-colors"
              data-testid="footer-gdpr"
            >
              Adatkezelési tájékoztató
            </button>
            <span className={THEME.divider}>•</span>
            <button 
              onClick={() => openLegalModal('aszf')} 
              className="hover:text-gold transition-colors"
              data-testid="footer-aszf"
            >
              ÁSZF
            </button>
          </div>
          
          <p className={`text-sm ${THEME.textDim}`}>
            © 2026 TR Fodrászat | Debrecen
          </p>
        </div>
      </div>
    </footer>
  );
};



const BookingModal = ({ isOpen, onClose }) => {
    // 1. Definiáljuk a két különböző linket (használd az ID-dat a kép alapján: 1061152163748818)
  const isMobile = typeof window !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent);
  const messengerLink = isMobile 
    ? "https://m.me/1061152163748818" // Mobilon megnyitja az appot
    : "https://www.facebook.com/messages/t/1061152163748818"; // PC-n a direkt chatet nyitja
  const handlePhoneClick = (e) => {
    if (!isMobile) {
      // Ha NEM mobilról nézik, megállítjuk a folyamatot
      e.preventDefault();
      alert("Telefonhíváshoz nyisd meg a weboldalt a telefonodon!");
    }
  };
  // Booking Modal
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${THEME.bgCard} border ${THEME.border} max-w-md rounded-none`} data-testid="booking-modal">
        <DialogHeader className="flex flex-col items-center text-center">
          <div className="mx-auto mb-6 p-4 bg-gold/10 w-fit">
            <Scissors className="w-10 h-10 text-gold" />
          </div>
          <DialogTitle className={`font-serif text-2xl ${THEME.textPrimary} text-center w-full`}>Időpontfoglalás</DialogTitle>
          <DialogDescription className={`${THEME.textSecondary} text-center w-full`}>
            Válassz az alábbi lehetőségek közül
          </DialogDescription>
        </DialogHeader>
        
        
        
        <div className="space-y-4 mt-6">
          
          <a 
            /*href="https://www.facebook.com/messages/t/1061152163748818"*/
            /*href="https://m.me/1061152163748818"*/
            href={messengerLink}
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full bg-gold text-black font-sans font-bold text-sm tracking-[0.1em] uppercase px-6 py-4 hover:bg-gold-dark transition-colors"
            data-testid="booking-messenger-btn"
          >
            <MessageCircle className="w-5 h-5" /> Üzenet Messengeren
          </a>
          
          <a 
            href="tel:+36501065988"
            onClick={handlePhoneClick}
            className="flex items-center justify-center gap-3 w-full border border-gold text-gold font-sans font-bold text-sm tracking-[0.1em] uppercase px-6 py-4 hover:bg-gold/10 transition-colors"
            data-testid="booking-phone-btn"
          >
            <Phone className="w-5 h-5" /> Hívás: +36 50 106 59 88
          </a>
        </div>
        
        <p className={`text-center ${THEME.textMuted} text-sm mt-6`}>
          Telefonon bármikor elérsz!
        </p>
      </DialogContent>
    </Dialog>
  );
};

// Legal Modal
const LegalModal = ({ isOpen, onClose, type }) => {
  const content = {
    impresszum: {
      title: "Impresszum",
      body: (
        <>
          <p><strong>TR Fodrászat</strong></p>
          <p><strong>Szolgáltató neve:</strong> Tóth Róbert EV</p>
          <p><strong>Székhely:</strong> 4031 Debrecen, Derék utca 36.</p>
          <p><strong>Elérhetőség:</strong> +36 50 106 59 88</p>
          <p><strong>Email:</strong> trfodraszat@gmail.com</p>
        </>
      )
    },
    adatkezeles: {
      title: "Adatkezelési Tájékoztató (GDPR)",
      body: (
        <>
      <p className="font-semibold italic">
        A TR Fodrászat (Tóth Róbert EV) tiszteletben tartja a látogatók személyes adatainak védelméhez való jogát, és azokat a GDPR előírásaival összhangban kezeli.
      </p>

      <div className="mt-6">
        <h4 className="font-bold border-b border-gray-200 pb-1">1. Az Adatkezelő adatai</h4>
        <p className="mt-1"><strong>Név:</strong> Tóth Róbert EV.</p>
        <p><strong>Székhely:</strong> 4031 Debrecen, Derék utca 36 - 7/23.</p>
        <p><strong>E-mail:</strong> info@trfodraszat.hu</p>
        <p><strong>Telefonszám:</strong> +36 50 106 59 88</p>
      </div>

      <div className="mt-4">
        <h4 className="font-bold border-b border-gray-200 pb-1">2. Az adatkezelés célja</h4>
        <p className="mt-1">
          A weboldal működésének biztosítása, a felhasználói élmény javítása, időpontok egyeztetése, valamint statisztikai és marketing célú elemzések készítése.
        </p>
      </div>

      <div className="mt-4">
        <h4 className="font-bold border-b border-gray-200 pb-1">3. Kezelt adatok köre</h4>
        <p className="mt-1">
          A látogatás során automatikusan rögzített adatok (IP-cím, böngésző típusa) és az időpontfoglalás során önkéntesen megadott azonosító adatok.
        </p>
      </div>

      <div className="mt-4">
        <h4 className="font-bold border-b border-gray-200 pb-1">4. Sütik (Cookies) és nyomkövetés</h4>
        <p className="mt-1">A weboldal az alábbi harmadik féltől származó kódokat alkalmazza:</p>
        <ul className="list-disc ml-5 mt-2 space-y-1 text-sm">
          <li><strong>Google Analytics:</strong> Látogatottsági és viselkedési statisztikák.</li>
          <li><strong>Google Ads & Facebook Pixel:</strong> Hirdetések célzása és hatékonyságmérése.</li>
        </ul>
      </div>

      <div className="mt-4">
        <h4 className="font-bold border-b border-gray-200 pb-1">5. Az adatkezelés jogalapja</h4>
        <p className="mt-1">
          A felhasználó kifejezett hozzájárulása (GDPR 6. cikk (1) bekezdés a) pont), illetve szerződéses kötelezettség teljesítése (időpontfoglalás).
        </p>
      </div>

      <div className="mt-4">
        <h4 className="font-bold border-b border-gray-200 pb-1">6. Az adatok továbbítása</h4>
        <p className="mt-1 leading-relaxed">
          Az adatok a fent említett szolgáltatók (Google LLC, Meta Platforms Inc.) EU-n kívüli szervereire is kerülhetnek, melyek garantálják a GDPR-nak megfelelő adatvédelmi szintet.
        </p>
      </div>

      <div className="mt-4">
        <h4 className="font-bold border-b border-gray-200 pb-1">7. Adatmegőrzés</h4>
        <p className="mt-1">
          A sütik érvényességi ideje változó; az analitikai adatok a Google Analytics rendszerében 26 hónap után automatikusan törlődnek.
        </p>
      </div>

      <div className="mt-4">
        <h4 className="font-bold border-b border-gray-200 pb-1">8. Az érintettek jogai</h4>
        <p className="mt-1 text-sm leading-relaxed">
          Ön jogosult tájékoztatást kérni, adatai helyesbítését vagy törlését kezdeményezni. Panasz esetén fordulhat az Adatkezelőhöz vagy a <strong>NAIH</strong> hatósághoz.
        </p>
      </div>

      <div className="mt-4">
        <h4 className="font-bold border-b border-gray-200 pb-1">9. Kapcsolat</h4>
        <p className="mt-1 font-semibold text-blue-800">info@trfodraszat.hu</p>
      </div>

      <p className="mt-6 text-[10px] text-gray-400 italic text-center">
        Utolsó módosítás: 2026. május
      </p>
    </>
      )
    },
   aszf: {
      title: "Általános Szerződési Feltételek (ÁSZF)",
      body: (
        <>
          <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-8 text-[13px] leading-relaxed text-gray-900 text-justify font-serif">
            
            {/* Preambulum */}
            <div className="text-center border-b-2 border-black pb-6">
              <h2 className="text-xl font-black uppercase tracking-tighter">Általános Szerződési Feltételek</h2>
              <p className="text-[11px] mt-2 font-bold uppercase">TR Fodrászat –</p>
              <p className="text-[10px] mt-1 italic">Érvényes: 2026. május 01</p>
            </div>

            {/* 1. Értelmező rendelkezések és a Szolgáltató azonosítása */}
            <section>
              <h3 className="font-bold border-b border-gray-300 mb-2 uppercase">1. A Szolgáltató jogállása és adatai</h3>
              <p className="mb-2">Jelen szabályzat (a továbbiakban: ÁSZF) rögzíti a Szolgáltató (a továbbiakban: <strong>Szolgáltató</strong>) és a szolgáltatást igénybe vevő természetes személy (a továbbiakban: <strong>Vendég</strong>) közötti jogviszony általános feltételeit.</p>
              <div className="grid gap-1 pl-4 italic bg-gray-50 p-3 border">
                <p><span className="font-bold">Név:</span> __________________________</p>
                <p><span className="font-bold">Székhely:</span> __________________________</p>
                <p><span className="font-bold">Adószám:</span> __________________________</p>
                <p><span className="font-bold">Nyilvántartási szám:</span> __________________________</p>
                <p><span className="font-bold">Kapcsolattartás:</span> __________________________</p>
              </div>
            </section>

            {/* 2. A szerződés létrejötte */}
            <section>
              <h3 className="font-bold border-b border-gray-300 mb-2 uppercase">2. A szerződéses jogviszony keletkezése</h3>
              <p>
                A felek közötti szolgáltatási szerződés a Vendég által kezdeményezett időpontfoglalás Szolgáltató általi visszaigazolásával jön létre. Az időpontfoglalással a Vendég elismeri, hogy jelen ÁSZF rendelkezéseit megismerte, azokat kifejezetten elfogadja, és magára nézve kötelezőnek ismeri el (Ptk. 6:77. §).
              </p>
            </section>

            {/* 3. Rendelkezésre állási díj és Lemondási feltételek */}
            <section className="bg-gray-100 p-4 border-l-4 border-black">
              <h3 className="font-black mb-2 uppercase">3. Rendelkezésre állási díj és kötbérigény</h3>
              <p className="mb-2 font-semibold">A Szolgáltató az egyeztetett időpontban kizárólagos rendelkezésre állást biztosít a Vendég részére.</p>
              <p className="mb-2">
                3.1. <strong>Lemondás:</strong> A Vendég jogosult az időpontot a kezdést megelőzően legalább <strong>24 órával</strong> jogkövetkezmények nélkül lemondani.
              </p>
              <p className="mb-2 text-red-700 font-bold">
                3.2. Szerződésszegés (No-Show): Amennyiben a Vendég az időpontot 24 órán belül mondja le, vagy tájékoztatás nélkül nem jelenik meg, a Szolgáltató jogosult a kiesett munkaidő okán "Rendelkezésre állási díj" érvényesítésére.
              </p>
              <p>
                3.3. A Szolgáltató fenntartja a jogot, hogy ismételt szerződésszegés esetén a jövőbeni szolgáltatásokat <strong>100%-os előre történő díjfizetéshez</strong> kösse, vagy a szolgáltatási igényt indoklás nélkül elutasítsa.
              </p>
            </section>

            {/* 4. Késedelemmel kapcsolatos jogkövetkezmények */}
            <section>
              <h3 className="font-bold border-b border-gray-300 mb-2 uppercase">4. A Vendég késedelme</h3>
              <p>
                A Vendég késedelmes megjelenése a szerződés teljesítését veszélyezteti. 15 percet meghaladó késés esetén a Szolgáltató jogosult a szerződéstől elállni, és a 3.2. pont szerinti jogkövetkezményeket alkalmazni, vagy a szolgáltatás tartalmát a hátralévő időre redukálni a teljes szolgáltatási díj fenntartása mellett.
              </p>
            </section>

            {/* 5. Díjazás és fizetési feltételek */}
            <section>
              <h3 className="font-bold border-b border-gray-300 mb-2 uppercase">5. Ellenszolgáltatás és teljesítés</h3>
              <p>
                A szolgáltatás ellenértéke a Szolgáltató mindenkor hatályos árlistája alapján kerül meghatározásra. A kifizetés a szolgáltatás teljesítését követően, haladéktalanul esedékes.
              </p>
              <p className="font-bold mt-2 underline">Fizetési mód: Kizárólag készpénzes teljesítés (HUF).</p>
            </section>

            {/* 6. Felelősségkorlátozás és kizárás */}
            <section className="space-y-2">
              <h3 className="font-bold border-b border-gray-300 mb-2 uppercase">6. Kárfelelősség és szavatosság</h3>
              <p>
                6.1. <strong>Tájékoztatási kötelezettség:</strong> A Vendég köteles a Szolgáltatót tájékoztatni minden olyan releváns körülményről (pl. gyógyszerszedés, allergia, bőrbetegség), amely a szolgáltatás kimenetelét befolyásolhatja. E tájékoztatás elmaradásából eredő károkért a Szolgáltató a felelősségét kifejezetten kizárja.
              </p>
              <p>
                6.2. <strong>Utókezelés:</strong> A nem megfelelő otthoni ápolásból, szakszerűtlen kezelésből eredő esztétikai vagy egészségügyi károkért a Szolgáltató nem tartozik helytállási kötelezettséggel.
              </p>
            </section>

            {/* 7. Adatvédelem (GDPR) */}
            <section>
              <h3 className="font-bold border-b border-gray-300 mb-2 uppercase">7. Adatkezelési nyilatkozat</h3>
              <p className="text-[11px]">
                A Szolgáltató a Vendég adatait (név, elérhetőség) a GDPR (2016/679/EU) előírásai szerint, kizárólag a szerződés teljesítése és kapcsolattartás céljából kezeli. Az adatok harmadik fél részére – jogszabályi kötelezettség hiányában – nem kerülnek továbbításra.
              </p>
            </section>

            {/* 8. Záró rendelkezések */}
            <section className="border-t-2 border-black pt-4">
              <h3 className="font-bold mb-2 uppercase text-[11px]">8. Irányadó jog és jogviták</h3>
              <p className="text-[11px]">
                Jelen ÁSZF-ben nem szabályozott kérdésekben a Polgári Törvénykönyv (2013. évi V. törvény) és a fogyasztóvédelemre vonatkozó hatályos magyar jogszabályok irányadók. Jogvita esetén a felek törekszenek a peren kívüli egyezségre, ennek sikertelensége esetén alávetik magukat a Szolgáltató székhelye szerint illetékes bíróság kizárólagos illetékességének.
              </p>
            </section>

            <div className="text-center text-[10px] font-bold opacity-50 uppercase pt-4">
              Dokumentum vége
            </div>
          </div>
        </>
      )
    }
  };
  

  const currentContent = content[type] || content.impresszum;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${THEME.bgCard} border ${THEME.border} max-w-lg rounded-none max-h-[80vh] overflow-y-auto`} data-testid={`legal-modal-${type}`}>
        <DialogHeader>
          <DialogTitle className={`font-serif text-xl ${THEME.textPrimary} pr-12 leading-tight`}>{currentContent.title}</DialogTitle>
        </DialogHeader>
        <div className={`${THEME.textSecondary} text-sm leading-relaxed mt-4`}>
          {currentContent.body}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Cookie Banner frissített verzió
const CookieBanner = ({ onAccept, onDecline }) => {
  return (
    <motion.div 
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className={`sticky bottom-0 left-0 right-0 z-50 ${THEME.bgCard} border-t border-gold/30 p-4 sm:p-6 shadow-lg`}
      data-testid="cookie-banner"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className={`text-sm ${THEME.textSecondary} text-center sm:text-left`}>
          Weboldalunk sütiket használ a témabeállítások mentéséhez és a jobb felhasználói élményért.
        </p>
        
        <div className="flex gap-3 w-full sm:w-auto">
          {/*Elutasító gomb */}
          <button 
            onClick={onDecline}
            className="flex-1 sm:flex-none border-2 border-[#705403] text-[#705403] font-sans font-bold text-xs tracking-widest uppercase px-6 py-3 hover:bg-[#705403] hover:text-white transition-all whitespace-nowrap"
            data-testid="cookie-decline-btn"
          >
            Elutasítom
          </button>

          {/* Elfogadó gomb */}
          <button 
            onClick={onAccept}
            className="flex-1 sm:flex-none bg-gold text-black font-sans font-bold text-[10px] tracking-[0.1em] uppercase px-6 py-3 hover:bg-gold-dark transition-colors whitespace-nowrap"
            data-testid="cookie-accept-btn"
          >
            Elfogadom
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Floating Call Button (Mobile only)
const FloatingCallButton = () => {
  return (
    <a 
      href="tel:+36501065988"
      className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 w-14 h-14 bg-gold text-black rounded-full flex items-center justify-center shadow-lg shadow-gold/30 hover:bg-gold-dark transition-colors lg:hidden z-40"
      data-testid="floating-call-btn"
      aria-label="Telefonhívás"
    >
      <Phone className="w-6 h-6" />
    </a>
  );
};

// Main App Component
function App() {
  const [scrolled, setScrolled] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [legalModal, setLegalModal] = useState({ open: false, type: 'impresszum' });
  const [showCookieBanner, setShowCookieBanner] = useState(() => {
    return localStorage.getItem('cookie-consent') === null;
  });
  const handleAcceptCookies = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    setShowCookieBanner(false);
  };
  const handleDeclineCookies = () => {
    localStorage.setItem('cookie-consent', 'declined');
    setShowCookieBanner(false);
  };

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Theme management - default is LIGHT
  useEffect(() => {
    const savedTheme = localStorage.getItem('tr_theme');
    const shouldBeDark = savedTheme === 'dark';
    setIsDark(shouldBeDark);
    document.documentElement.classList.toggle('dark', shouldBeDark);
  }, []);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    document.documentElement.classList.toggle('dark', newIsDark);
    localStorage.setItem('tr_theme', newIsDark ? 'dark' : 'light');
  };

  // Cookie consent
  useEffect(() => {
    const cookiesAccepted = localStorage.getItem('cookiesAccepted');
    if (!cookiesAccepted) {
      setTimeout(() => setShowCookieBanner(true), 1500);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    setShowCookieBanner(false);
  };

  const openBooking = () => setBookingOpen(true);
  const closeBooking = () => setBookingOpen(false);

  const openLegalModal = (type) => setLegalModal({ open: true, type });
  const closeLegalModal = () => setLegalModal({ ...legalModal, open: false });

  return (
    <div className={`min-h-screen ${THEME.bgPrimary}`} data-testid="app-container">
      <Navigation 
        scrolled={scrolled} 
        isDark={isDark} 
        toggleTheme={toggleTheme}
        openBooking={openBooking}
      />
      
      <main>
        <HeroSection openBooking={openBooking} />
        <AboutSection />
        <FeaturesSection />
        <ServicesSection />
        <TestimonialsSection />
        <GallerySection />
        <LocationSection />
        <FAQSection />
      </main>
      
      <Footer openLegalModal={openLegalModal} />
      
      {/* Modals */}
      <BookingModal isOpen={bookingOpen} onClose={closeBooking} />
      <LegalModal isOpen={legalModal.open} onClose={closeLegalModal} type={legalModal.type} />
      
      {/* Cookie Banner */}
      <AnimatePresence>
        {showCookieBanner && (
          <CookieBanner 
            onAccept={handleAcceptCookies} 
            onDecline={handleDeclineCookies} 
          />
        )}
      </AnimatePresence>
      
      {/* Floating Call Button */}
      <FloatingCallButton />
    </div>
  );
}

export default App;
